/**
 * Structural analysis (MICMAC / network centrality) for the impact-path DAG.
 *
 * Pure, runtime, no I/O — feed it the nodes + multi-parent connections already
 * loaded by the pivot view and it returns reach metrics and a MICMAC
 * influence×dependence classification. This is the STRUCTURAL counterpart to the
 * semantic pivot points (embedding clusters): "which nodes are structurally
 * central?" rather than "which themes recur?".
 *
 * IMPORTANT — why TRANSITIVE reach, not immediate degree:
 * An impact-path tree gives every node exactly one parent, so immediate
 * in-degree is ≡1 and the MICMAC dependence axis collapses. We instead use
 * transitive reach, which is also truer to MICMAC (the name = cross-impact
 * matrix MULTIPLICATION = indirect influence):
 *   influence  (motricité)  = # of downstream descendants (subtree reach)
 *   dependence (dépendance) = # of upstream ancestors (causal depth)
 * Split at the mean of each axis into four quadrants:
 *   driving    — high influence, low dependence  (input/explanatory: variables)
 *   relay      — high influence, high dependence (critical leverage — unstable)
 *   dependent  — low influence, high dependence  (outcome/result nodes)
 *   autonomous — low influence, low dependence   (shallow leaves)
 *
 * The "relay" quadrant is where Meadows-style leverage lives: change a relay
 * node and the effect both propagates widely downstream AND sits deep in the
 * causal chain. Scenario starter variables fall into "driving", which is correct.
 */

export const QUADRANTS = {
  DRIVING: 'driving',
  RELAY: 'relay',
  DEPENDENT: 'dependent',
  AUTONOMOUS: 'autonomous',
};

export const QUADRANT_META = {
  driving: { label: 'Ohjaavat', hint: 'Suuri vaikutus, pieni riippuvuus — syöttömuuttujat', color: '#2196F3' },
  relay: { label: 'Releet / kriittiset', hint: 'Suuri vaikutus JA riippuvuus — vipuvarret', color: '#FFD93D' },
  dependent: { label: 'Riippuvat', hint: 'Pieni vaikutus, suuri riippuvuus — tulosmuuttujat', color: '#4CAF50' },
  autonomous: { label: 'Itsenäiset', hint: 'Heikosti kytketyt — matalat lehtisolmut', color: '#9E9E9E' },
};

const QUADRANT_META_EN = {
  driving: { label: 'Driving', hint: 'High influence, low dependence — input variables', color: '#2196F3' },
  relay: { label: 'Relay / critical', hint: 'High influence AND dependence — leverage points', color: '#FFD93D' },
  dependent: { label: 'Dependent', hint: 'Low influence, high dependence — outcome variables', color: '#4CAF50' },
  autonomous: { label: 'Autonomous', hint: 'Weakly coupled — shallow leaf nodes', color: '#9E9E9E' },
};

const QUADRANT_META_SV = {
  driving: { label: 'Styrande', hint: 'Stor påverkan, låg beroende — inputvariabler', color: '#2196F3' },
  relay: { label: 'Relä / kritiska', hint: 'Stor påverkan OCH beroende — hävstångspunkter', color: '#FFD93D' },
  dependent: { label: 'Beroende', hint: 'Liten påverkan, stort beroende — utfallsvariabler', color: '#4CAF50' },
  autonomous: { label: 'Självständiga', hint: 'Svagt kopplade — grunda lövnoder', color: '#9E9E9E' },
};

/** Localized quadrant chrome for MICMAC views. */
export function getQuadrantMeta(language = 'fi') {
  const lang = String(language || 'fi').toLowerCase().slice(0, 2);
  if (lang === 'en') return QUADRANT_META_EN;
  if (lang === 'sv') return QUADRANT_META_SV;
  return QUADRANT_META;
}

const unnamedNodeByLang = {
  fi: '(nimetön solmu)',
  en: '(unnamed node)',
  sv: '(namnlös nod)',
};

const labelOf = (n, language = 'fi') =>
  n?.title || n?.text || unnamedNodeByLang[String(language || 'fi').slice(0, 2)] || unnamedNodeByLang.fi;
const isStarter = (n) => Boolean(n?.is_scenario_starter) || n?.depth === 0;
const mean = (nums) => (nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0);

/** Build adjacency: parent→children and child→parents, from tree edges + connections. */
function buildAdjacency(nodes, connections) {
  const children = new Map();
  const parents = new Map();
  const ids = new Set(nodes.map((n) => n.id));
  const link = (p, c) => {
    if (!p || !c || p === c || !ids.has(p) || !ids.has(c)) return;
    if (!children.has(p)) children.set(p, new Set());
    if (!parents.has(c)) parents.set(c, new Set());
    children.get(p).add(c);
    parents.get(c).add(p);
  };
  nodes.forEach((n) => link(n.parent_id || n.parentId, n.id));
  connections.forEach((c) => link(c.parentId || c.parent_node_id, c.childId || c.child_node_id));
  return { children, parents };
}

/** Transitive reach count from `id` following `adj` (memoised, cycle-safe). */
function reachCount(id, adj, memo) {
  if (memo.has(id)) return memo.get(id).size;
  const set = new Set();
  memo.set(id, set); // set before recursing → guards cycles
  for (const next of adj.get(id) || []) {
    if (next === id) continue;
    set.add(next);
    reachCount(next, adj, memo);
    for (const r of memo.get(next)) set.add(r);
  }
  return set.size;
}

/**
 * Compute per-node structural reach metrics + MICMAC quadrants.
 *
 * @param {Array} nodes        impact-path nodes (id, title/text, parent_id, depth, is_scenario_starter)
 * @param {Array} connections  multi-parent connections ({parentId/parent_node_id, childId/child_node_id})
 * @param {{ language?: string }} [opts]
 * @returns {{ nodes, summary, byQuadrant }}
 */
export function computeStructuralMetrics(nodes = [], connections = [], opts = {}) {
  const language = opts.language || 'fi';
  const { children, parents } = buildAdjacency(nodes, connections);

  // Immediate degree — only used for the convergence (borrowed-node) stat.
  const inDegree = new Map();
  parents.forEach((set, id) => inDegree.set(id, set.size));

  // Transitive reach — the MICMAC axes.
  const descMemo = new Map();
  const ancMemo = new Map();

  const enriched = nodes.map((n) => ({
    id: n.id,
    label: labelOf(n, language),
    isStarter: isStarter(n),
    influence: reachCount(n.id, children, descMemo), // downstream descendants
    dependence: reachCount(n.id, parents, ancMemo),  // upstream ancestors
    raw: n,
  }));

  // Split each axis at its mean over CONNECTED nodes.
  const connected = enriched.filter((e) => e.influence > 0 || e.dependence > 0);
  const infThreshold = mean(connected.map((e) => e.influence));
  const depThreshold = mean(connected.map((e) => e.dependence));

  enriched.forEach((e) => {
    const hiInf = e.influence > infThreshold;
    const hiDep = e.dependence > depThreshold;
    e.quadrant = hiInf
      ? (hiDep ? QUADRANTS.RELAY : QUADRANTS.DRIVING)
      : (hiDep ? QUADRANTS.DEPENDENT : QUADRANTS.AUTONOMOUS);
  });

  const byQuadrant = { driving: [], relay: [], dependent: [], autonomous: [] };
  enriched.forEach((e) => byQuadrant[e.quadrant].push(e));

  const edgeCount =
    nodes.filter((n) => n.parent_id || n.parentId).length + connections.length;

  return {
    nodes: enriched,
    summary: {
      nodeCount: nodes.length,
      edgeCount,
      multiParentEdges: connections.length,
      convergenceCount: [...inDegree.values()].filter((d) => d >= 2).length,
      infThreshold,
      depThreshold,
      quadrantCounts: {
        driving: byQuadrant.driving.length,
        relay: byQuadrant.relay.length,
        dependent: byQuadrant.dependent.length,
        autonomous: byQuadrant.autonomous.length,
      },
    },
    byQuadrant,
  };
}

/**
 * Nodes ranked as structural leverage candidates — by downstream reach
 * (influence), relay quadrant first. Optionally drop scenario starters so
 * participant-generated consequences surface.
 */
export function rankLeverageCandidates(enrichedNodes = [], { excludeStarters = false, limit = 12 } = {}) {
  const quadrantRank = { relay: 0, driving: 1, dependent: 2, autonomous: 3 };
  return enrichedNodes
    .filter((e) => (excludeStarters ? !e.isStarter : true))
    .filter((e) => e.influence > 0 || e.dependence > 0)
    .sort((a, b) => {
      const q = quadrantRank[a.quadrant] - quadrantRank[b.quadrant];
      if (q !== 0) return q;
      return b.influence - a.influence || b.dependence - a.dependence;
    })
    .slice(0, limit);
}
